import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Shield, Loader2, ArrowRight, ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function GoogleButton({ label = "Google ile Devam Et", testId = "google-signin-btn" }) {
  return (
    <button
      type="button"
      onClick={startGoogleLogin}
      data-testid={testId}
      className="w-full flex items-center justify-center gap-2.5 border border-slate-700 bg-slate-900/40 hover:border-[#00E5FF]/50 hover:bg-slate-900/70 transition px-4 py-2.5 text-sm font-medium text-slate-100"
    >
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.4 6 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.4 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.4 6 29.5 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.5l-6.5-5.5C29.4 34.6 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.5 5.5c-.4.4 7.5-5.5 7.5-15.7 0-1.2-.1-2.4-.4-3.5z"/>
      </svg>
      {label}
    </button>
  );
}

export default function LoginPage() {
  const [mode, setMode] = useState("login"); // login | register | registered
  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent" />

      <div className="relative w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 border border-[#00E5FF]/40 bg-[#00E5FF]/10 flex items-center justify-center">
            <Shield size={20} className="text-[#00E5FF]" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-heading text-xl font-black tracking-tight">GACRF<span className="text-[#00E5FF]">®</span></div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Cyber Resilience Platform</div>
          </div>
        </div>

        <div className="card p-8 card-accent">
          {mode === "login" && <LoginForm onSwitch={() => setMode("register")} />}
          {mode === "register" && <RegisterForm onSwitch={() => setMode("login")} onSuccess={() => setMode("registered")} />}
          {mode === "registered" && <RegisteredNotice onBack={() => setMode("login")} />}
        </div>

        <div className="mt-6 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600">
          GACRF Platform · v2.0 · GlobeSec Labs
        </div>
      </div>
    </div>
  );
}

function LoginForm({ onSwitch }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const r = await login(email.trim(), password);
    if (!r.ok) setErr(r.error);
    setLoading(false);
  };

  return (
    <>
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">Secure Access</div>
        <h1 className="font-heading text-2xl font-bold">Sign in to your workspace</h1>
        <p className="text-sm text-slate-400 mt-2">
          Authorized access only. All session events are recorded.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Email</label>
          <input data-testid="login-email" type="email" required autoFocus
            value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Password</label>
          <input data-testid="login-password" type="password" required minLength={6}
            value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" />
        </div>

        {err && (
          <div data-testid="login-error" className="text-xs text-red-400 border border-red-500/20 bg-red-500/5 px-3 py-2 font-mono">
            {err}
          </div>
        )}

        <button data-testid="login-submit" className="btn btn-primary w-full justify-center" type="submit" disabled={loading}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : null}
          {loading ? "Authenticating..." : "Sign In"}
        </button>
      </form>

      <div className="mt-5 flex items-center gap-3">
        <div className="flex-1 h-px bg-slate-800/70" />
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600">veya</div>
        <div className="flex-1 h-px bg-slate-800/70" />
      </div>

      <div className="mt-4">
        <GoogleButton label="Google ile Giriş Yap" testId="google-signin-btn" />
      </div>

      <div className="mt-6 pt-6 border-t border-slate-800/70">
        <div className="text-xs text-slate-400 text-center">
          Hesabınız yok mu?{" "}
          <button onClick={onSwitch} className="text-[#00E5FF] hover:underline font-semibold" data-testid="switch-to-register">
            Kayıt olun <ArrowRight size={11} className="inline" />
          </button>
        </div>
      </div>
    </>
  );
}

function RegisterForm({ onSwitch, onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", role_requested: "viewer", notes: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/register", form);
      onSuccess();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <>
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">Create Account</div>
        <h1 className="font-heading text-2xl font-bold">Kayıt talebi oluşturun</h1>
        <p className="text-sm text-slate-400 mt-2">
          Hesabınız SuperAdmin onayı sonrası aktifleşir ve size uygun bir tenant atanır.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Ad Soyad</label>
          <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Kurumsal Email</label>
          <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Parola (≥ 8 karakter)</label>
          <input required type="password" minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Şirket / Kurum</label>
          <input required value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Acme Corp." data-testid="register-company" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Talep Edilen Rol</label>
          <select value={form.role_requested} onChange={(e) => set("role_requested", e.target.value)} data-testid="register-role">
            <option value="viewer">Viewer (yalnızca okuma)</option>
            <option value="auditor">Auditor (skorlama + kanıt yükleme)</option>
            <option value="admin">Tenant Admin (tam yetki)</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Notlar (opsiyonel)</label>
          <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="SuperAdmin için ek bilgi..." data-testid="register-notes" />
        </div>

        {err && (
          <div className="text-xs text-red-400 border border-red-500/20 bg-red-500/5 px-3 py-2 font-mono">{err}</div>
        )}

        <button className="btn btn-primary w-full justify-center" type="submit" disabled={loading} data-testid="register-submit">
          {loading ? <Loader2 size={14} className="animate-spin" /> : null}
          {loading ? "Gönderiliyor..." : "Kayıt Talebi Gönder"}
        </button>
      </form>

      <div className="mt-5 flex items-center gap-3">
        <div className="flex-1 h-px bg-slate-800/70" />
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600">veya</div>
        <div className="flex-1 h-px bg-slate-800/70" />
      </div>
      <div className="mt-4">
        <GoogleButton label="Google ile Kayıt Ol" testId="google-signup-btn" />
      </div>

      <div className="mt-6 pt-6 border-t border-slate-800/70">
        <div className="text-xs text-slate-400 text-center">
          Zaten hesabınız var mı?{" "}
          <button onClick={onSwitch} className="text-[#00E5FF] hover:underline font-semibold" data-testid="switch-to-login">
            <ArrowLeft size={11} className="inline" /> Giriş yapın
          </button>
        </div>
      </div>
    </>
  );
}

function RegisteredNotice({ onBack }) {
  return (
    <div className="text-center py-4" data-testid="registered-notice">
      <div className="w-12 h-12 mx-auto border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center mb-4">
        <Shield size={22} className="text-emerald-400" strokeWidth={1.5} />
      </div>
      <h1 className="font-heading text-xl font-bold mb-2">Kayıt Talebiniz Alındı</h1>
      <p className="text-sm text-slate-400 mb-6 leading-relaxed">
        SuperAdmin talebinizi inceleyecek, uygun tenant&apos;a atayacak ve onayladığında bilgilendirileceksiniz.
        Onay sonrasında bu sayfadan giriş yapabilirsiniz.
      </p>
      <button className="btn btn-secondary w-full justify-center" onClick={onBack} data-testid="back-to-login">
        <ArrowLeft size={12} /> Giriş sayfasına dön
      </button>
    </div>
  );
}
